import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import AccionaFormItem from '../../../../../components/AccionaFormItem';
import AccionaInput from '../../../../../components/AccionaInput';
import Mixture from '../../../../../models/thermalControl/Mixture';
import style from './index.module.scss';
import AccionaButton from '../../../../../components/AccionaButton';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MainContext } from '../../../../../context/MainContext';
import AccionaAlert from '../../../../../components/AccionaAlert';
import AccionaSelect from '../../../../../components/AccionaSelect';
import { getUnusedDevices } from '../../../../../services/redux/thermalControl/devices/actions';
import MixtureDetailsModal from './components/MixtureDetailsModal';
import MixtureParams from './components/MixtureParams';
import { calculateFunction } from '../../../../../services/api/thermalControl/mixtures';
import AccionaConfirm from '../../../../../components/AccionaConfirm';
import AccionaSimpleSpinner from '../../../../../components/AccionaSimpleSpinner';
import AntdDatePicker from '../../../../../components/AntdDatePicker';
import AntdFormItem from '../../../../../components/AntdFormItem';
import dayjs from 'dayjs';
import { areObjectsEqual } from '../../../../../utils/utils';

const {
  wrapper,
  footer,
  main,
  mixtureDetails: mixtureDetailsLink,
  result,
} = style;

const CreateMixture = ({
  initialData,
  onFinish,
  onCancel,
  onFinishComplete,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { project, setLoading: setMainLoading } = useContext(MainContext);
  const { errorCreated } = useSelector(
    ({ thermalControlMixtures }) => thermalControlMixtures,
  );
  const { unusedList } = useSelector(
    ({ thermalControlDevices }) => thermalControlDevices,
  );
  const [showMixtureDetailsModal, setShowMixtureDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [mixture, setMixture] = useState(
    initialData || new Mixture({ project: project.id_project }),
  );
  const [calibrationResult, setCalibrationResult] = useState();
  const [calculating, setCalculating] = useState(false);

  const needToBeSavedFirst = useMemo(() => {
    return (
      !initialData ||
      mixture.start_time !== initialData.start_time ||
      mixture.device !== initialData.device ||
      mixture.maturity_method !== initialData.maturity_method
    );
  }, [initialData, mixture]);

  const hasTheMixtureChange = useMemo(() => {
    return initialData && !areObjectsEqual(initialData, mixture);
  }, [initialData, mixture]);

  const parseMixture = () => {
    return new Mixture({
      ...mixture,
      project: initialData?.project || project.id_project,
    });
  };

  const onAddClick = () => {
    const parsedMixture = parseMixture();
    onFinish(parsedMixture);
  };

  const onFinishClick = () => {
    setShowConfirmModal({
      message: intl.formatMessage({
        id: 'strength.thermalcontrol.mixtures.create.finishMixture.message',
      }),
      onConfirm: () => {
        const parsedMixture = parseMixture();
        onFinishComplete(parsedMixture);
        setShowConfirmModal();
      },
      onCancel: () => {
        setShowConfirmModal();
      },
    });
  };

  const handleOnOk = (details) => {
    setMixture((prev) => ({ ...prev, details }));
    setShowMixtureDetailsModal(false);
  };

  const handleOnParamsChange = useCallback(async (parameters) => {
    if (
      parameters.length &&
      parameters.every((param) =>
        Object.values(param).every((p) => p || p === 0),
      )
    ) {
      const { data } = await calculateFunction({
        parameters: parameters,
      });
      if (data.equation) {
        setCalibrationResult(data.equation);
      } else {
        setCalibrationResult('--');
      }
    } else {
      setCalibrationResult(null);
    }
    setMixture((prev) => ({ ...prev, parameters }));
    setCalculating(false);
  }, []);

  const handleOnWaiting = useCallback(() => {
    setCalculating(true);
  }, []);

  const formIsValidated = () => {
    return (
      mixture.name &&
      mixture.maturity_method &&
      mixture.device &&
      mixture.start_time
    );
  };

  useEffect(() => {
    if (errorCreated) {
      setMainLoading(false);
    }
  }, [setMainLoading, errorCreated]);

  useEffect(() => {
    dispatch(getUnusedDevices());
  }, [dispatch]);

  return (
    <div className={wrapper}>
      <div className={main}>
        <div>
          <AccionaFormItem
            extra={
              <span
                onClick={() => {
                  setShowMixtureDetailsModal(true);
                }}
                className={mixtureDetailsLink}
              >
                +{' '}
                {intl.formatMessage({
                  id: 'strength.thermalcontrol.mixtures.create.detailedDescription.link',
                })}
              </span>
            }
          >
            <AccionaInput
              state={{ value: mixture.name, status: [] }}
              label="name"
              onChange={(label, name) => {
                setMixture((prev) => ({ ...prev, name }));
              }}
            />
          </AccionaFormItem>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <AccionaFormItem style={{ marginRight: '40px' }}>
              <AccionaSelect
                state={{
                  value: mixture.maturity_method,
                  status: [],
                  rules: { required: true },
                }}
                value={mixture.maturity_method}
                label="maturity_method"
                onChange={(maturity_method) => {
                  if (mixture.parameters?.length) {
                    setShowConfirmModal({
                      message: intl.formatMessage({
                        id: 'strength.thermalcontrol.mixtures.create.resetDataAlert.message',
                      }),
                      onConfirm: () => {
                        setMixture((prev) => ({
                          ...prev,
                          maturity_method,
                          parameters: undefined,
                        }));
                        setShowConfirmModal();
                      },
                      onCancel: () => {
                        setShowConfirmModal();
                      },
                    });
                  } else {
                    setMixture((prev) => ({ ...prev, maturity_method }));
                  }
                }}
                options={[
                  {
                    label: intl.formatMessage({
                      id: 'maturityMethod.NurseSaul',
                    }),
                    value: 'NurseSaul',
                  },
                  {
                    label: intl.formatMessage({
                      id: 'maturityMethod.Arrhenius',
                    }),
                    value: 'Arrhenius',
                  },
                ]}
              />
            </AccionaFormItem>
            <AccionaFormItem>
              <AccionaSelect
                state={{
                  value: mixture.device,
                  status: [],
                  rules: { required: true },
                }}
                defaultValue={
                  unusedList.length || mixture.device
                    ? {
                        value: mixture.device,
                        label: mixture.device_data?.reference_number,
                      }
                    : {
                        label: `-- ${intl.formatMessage({
                          id: 'noUnusedDevices',
                        })} --`,
                      }
                }
                disabled={!unusedList.length}
                value={mixture.device}
                label="device"
                onChange={(device) => {
                  if (mixture.parameters?.length) {
                    setShowConfirmModal({
                      message: intl.formatMessage({
                        id: 'strength.thermalcontrol.mixtures.create.resetDataAlert.message',
                      }),
                      onConfirm: () => {
                        setMixture((prev) => ({
                          ...prev,
                          device,
                          parameters: undefined,
                        }));
                        setShowConfirmModal();
                      },
                      onCancel: () => {
                        setShowConfirmModal();
                      },
                    });
                  } else {
                    setMixture((prev) => ({ ...prev, device }));
                  }
                }}
                options={
                  unusedList?.map((device) => ({
                    label: device.reference_number,
                    value: device.id,
                  })) || []
                }
              />
            </AccionaFormItem>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <AntdFormItem
              label={intl.formatMessage({ id: 'start_time' })}
              layout="vertical"
              style={{ flex: 1 }}
            >
              <AntdDatePicker
                size="large"
                style={{ width: '100%', height: '45px' }}
                onChange={(date) => {
                  const updateDatePicker = () => {
                    setMixture((prev) => ({
                      ...prev,
                      start_time: date.toISOString(),
                      parameters: undefined,
                    }));
                  };
                  if (mixture.parameters?.length) {
                    setShowConfirmModal({
                      message: intl.formatMessage({
                        id: 'strength.thermalcontrol.mixtures.create.resetDataAlert.message',
                      }),
                      onConfirm: () => {
                        updateDatePicker();
                        setShowConfirmModal();
                      },
                      onCancel: () => {
                        setShowConfirmModal();
                      },
                    });
                  } else {
                    updateDatePicker();
                  }
                }}
                value={
                  mixture.start_time ? dayjs(mixture.start_time) : undefined
                }
                disabledDate={(current) => current.isAfter(dayjs(), 'minute')}
              />
            </AntdFormItem>
          </div>
          {!needToBeSavedFirst &&
            mixture.start_time &&
            mixture.device &&
            mixture.maturity_method && (
              <>
                <AntdFormItem
                  label={intl.formatMessage({ id: 'params' })}
                  layout="vertical"
                  style={{ marginTop: '20px' }}
                >
                  <MixtureParams
                    state={{
                      value: mixture.parameters,
                      status: [],
                      rules: { required: true },
                    }}
                    label="params"
                    mixtureId={mixture.id}
                    startDate={mixture.start_time}
                    method={mixture.maturity_method}
                    onChange={handleOnParamsChange}
                    onWaiting={handleOnWaiting}
                  />
                </AntdFormItem>
                <div className={result}>
                  {intl.formatMessage({
                    id: 'strength.thermalcontrol.mixtures.create.calibrationResult',
                  })}{' '}
                  {!calibrationResult ? (
                    <AccionaSimpleSpinner size={18} />
                  ) : (
                    <span>{calibrationResult}</span>
                  )}
                </div>
              </>
            )}
        </div>
        {errorCreated && (
          <AccionaAlert
            message={intl.formatMessage({
              id: 'strength.thermalcontrol.mixtures.create.error',
            })}
          />
        )}
      </div>
      <div className={`${footer}`}>
        <AccionaButton
          type="default"
          msg={intl.formatMessage({ id: 'cancel' })}
          onClick={onCancel}
        />
        <AccionaButton
          msg={intl.formatMessage({
            id: 'button.create.end',
          })}
          onClick={onFinishClick}
          disabled={
            !formIsValidated() ||
            !mixture.id ||
            hasTheMixtureChange ||
            calculating
          }
        />
        <AccionaButton
          msg={intl.formatMessage({
            id: initialData ? 'button.update.end' : 'button.add',
          })}
          onClick={onAddClick}
          disabled={!formIsValidated()}
        />
      </div>
      {showMixtureDetailsModal && (
        <MixtureDetailsModal
          open={showMixtureDetailsModal}
          mixtureDetails={mixture.details}
          onOk={handleOnOk}
          onCancel={() => setShowMixtureDetailsModal(false)}
        />
      )}
      {showConfirmModal && (
        <AccionaConfirm
          message={showConfirmModal.message}
          onCancel={showConfirmModal.onCancel}
          onConfirm={showConfirmModal.onConfirm}
        />
      )}
    </div>
  );
};

CreateMixture.propTypes = {
  initialData: PropTypes.instanceOf(Mixture),
  onFinish: PropTypes.func,
  onCancel: PropTypes.func,
  onFinishComplete: PropTypes.func,
  onSave: PropTypes.func,
};

CreateMixture.defaultProps = {
  initialData: undefined,
  onFinish: () => {},
  onCancel: () => {},
  onFinishComplete: () => {},
  onSave: () => {},
};

export default CreateMixture;
